export interface HuddleSpeakerTestSnapshot { testing: boolean; error?: string }
export const EMPTY_SPEAKER_TEST: HuddleSpeakerTestSnapshot = { testing: false };

export function supportsHuddleSpeakerSelection(): boolean {
  return typeof HTMLMediaElement !== 'undefined' && typeof HTMLMediaElement.prototype.setSinkId === 'function';
}

/** An explicit output must succeed before any sound is played; never silently fall back. */
export function setHuddleSpeaker(audio: HTMLAudioElement, deviceId?: string): Promise<void> | undefined {
  const sink = deviceId === 'default' ? '' : deviceId || '';
  if (typeof audio.setSinkId !== 'function') {
    if (sink) throw new DOMException('Speaker selection unavailable', 'NotSupportedError');
    return;
  }
  // New media elements already use the system default. Avoid an unnecessary async
  // hop so a default-speaker test retains the click's user activation in Safari.
  if (!sink && !audio.sinkId) return;
  return audio.setSinkId(sink);
}

export function huddleSpeakerError(error: unknown): string {
  const reason = error instanceof Error ? error.name : 'UnknownError';
  if (reason === 'NotSupportedError') return 'This browser uses your system default speaker. Choose System default to test it.';
  if (reason === 'NotAllowedError' || reason === 'SecurityError' || reason === 'InvalidStateError') return 'Speaker access was blocked. Check browser permissions or choose System default.';
  if (reason === 'NotFoundError') return 'That speaker is no longer available. Connect it or choose another speaker.';
  return 'Could not play the speaker test. Check your output device and try again.';
}

function speakerTone(): Blob {
  const rate = 22_050; const duration = 0.7; const frames = Math.round(rate * duration);
  const buffer = new ArrayBuffer(44 + frames * 2); const view = new DataView(buffer);
  const label = (offset: number, text: string) => [...text].forEach((letter, i) => view.setUint8(offset + i, letter.charCodeAt(0)));
  label(0, 'RIFF'); view.setUint32(4, buffer.byteLength - 8, true); label(8, 'WAVE'); label(12, 'fmt ');
  view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, 1, true);
  view.setUint32(24, rate, true); view.setUint32(28, rate * 2, true); view.setUint16(32, 2, true); view.setUint16(34, 16, true);
  label(36, 'data'); view.setUint32(40, frames * 2, true);
  for (let i = 0; i < frames; i += 1) {
    const time = i / rate; const fade = Math.min(1, time / 0.025, (duration - time) / 0.08);
    const wave = Math.sin(2 * Math.PI * 660 * time) + 0.35 * Math.sin(2 * Math.PI * 880 * time);
    view.setInt16(44 + i * 2, Math.round(wave * fade * 0.2 * 32_767), true);
  }
  return new Blob([buffer], { type: 'audio/wav' });
}

/** Local generated audio only: no capture, network request or call admission. */
export class HuddleSpeakerTest {
  readonly supported = supportsHuddleSpeakerSelection();
  private disposed = false;
  private version = 0;
  private audio?: HTMLAudioElement;
  private url?: string;
  private timer?: ReturnType<typeof setTimeout>;
  constructor(private readonly changed: (state: HuddleSpeakerTestSnapshot) => void) {}
  private emit(state: HuddleSpeakerTestSnapshot): void { if (!this.disposed) this.changed(state); }
  private release(): void {
    clearTimeout(this.timer); this.timer = undefined;
    if (this.audio) {
      this.audio.onended = null; this.audio.onerror = null;
      this.audio.pause(); this.audio.removeAttribute('src'); this.audio.load(); this.audio = undefined;
    }
    if (this.url) { URL.revokeObjectURL(this.url); this.url = undefined; }
  }
  async start(deviceId?: string): Promise<void> {
    if (this.disposed) return;
    this.stop(); const version = this.version; this.emit({ testing: true });
    try {
      const audio = new Audio(); this.audio = audio; audio.volume = 0.25;
      this.url = URL.createObjectURL(speakerTone()); audio.src = this.url;
      audio.onended = () => { if (version === this.version) this.stop(); };
      audio.onerror = () => {
        if (this.disposed || version !== this.version) return;
        this.version += 1; this.release(); this.emit({ testing: false, error: huddleSpeakerError(undefined) });
      };
      // Also bounds stalled routing/playback; a late browser promise cannot restart it.
      this.timer = setTimeout(() => { if (version === this.version) this.stop(); }, 2_000);
      const routed = setHuddleSpeaker(audio, deviceId);
      if (routed) await routed;
      if (this.disposed || version !== this.version) return;
      await audio.play();
    } catch (error) {
      if (this.disposed || version !== this.version) return;
      this.version += 1; this.release(); this.emit({ testing: false, error: huddleSpeakerError(error) });
    }
  }
  stop(): void { this.version += 1; this.release(); this.emit(EMPTY_SPEAKER_TEST); }
  dispose(): void { this.disposed = true; this.stop(); }
}
